import React from "react";
import { Users, Music, UserCheck, Eye, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLocation } from "wouter";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface FocusedViewSelectorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jamNightId: number;
}

type ViewType = "participants" | "on-stage" | "selected" | "consideration" | "stage-combined";

const VIEW_OPTIONS: Array<{
  id: ViewType;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
}> = [
  {
    id: "stage-combined",
    label: "Stage Overview",
    icon: Layers,
    description: "View on stage and selected together"
  },
  {
    id: "participants",
    label: "Participants",
    icon: Users,
    description: "View all signed-up participants"
  },
  {
    id: "on-stage",
    label: "On Stage",
    icon: Music,
    description: "View performers currently on stage"
  },
  {
    id: "selected",
    label: "Selected for Stage",
    icon: UserCheck,
    description: "View performers selected to go on next"
  },
  {
    id: "consideration",
    label: "Under Consideration",
    icon: Eye,
    description: "View performers being considered"
  }
];

export function FocusedViewSelector({ open, onOpenChange, jamNightId }: FocusedViewSelectorProps) {
  const [, setLocation] = useLocation();

  const handleViewSelect = (viewType: ViewType) => {
    // Navigate to focused view with the selected view
    setLocation(`/event/${jamNightId}/focused/${viewType}`);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Select View</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3 py-4">
          {VIEW_OPTIONS.map((option) => {
            const Icon = option.icon;
            return (
              <button
                key={option.id}
                onClick={() => handleViewSelect(option.id)}
                className="flex items-start gap-3 p-4 rounded-lg border border-white/20 bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-all duration-200 text-left"
              >
                <Icon className="h-5 w-5 mt-0.5 text-slate-700 flex-shrink-0" />
                <div className="flex-1">
                  <div className="font-medium text-slate-900">{option.label}</div>
                  <div className="text-sm text-slate-600 mt-0.5">{option.description}</div>
                </div>
              </button>
            );
          })}
        </div>
        <div className="flex justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}